/**
 * The hits the page draws numbers for (P2.1-T4, question 5: only the hits the player deals and takes), handed from
 * the combat frame to the damage numbers surface (`world-ui/damage-numbers/`) through a ring of records made once,
 * so a hit costs no allocation between its event and its number. A negated hit carries its cause (ADR-079 decision 6),
 * whose word the surface draws in place of the number, and a heal says it is one, which the surface draws green and
 * signed "+" (ADR-079's amendment for P1.9-T66): a `heal` event the page's character receives or gives (ADR-091
 * decision 11).
 */

/**
 * One hit waiting for its number.
 */
export interface HitReading {
  target: number;
  amount: number;
  critical: boolean;
  taken: boolean;
  damageType: number;
  cause: number;
  heal: boolean;
}

/**
 * A bounded ring of hits. More hits between two drains than it holds keep the newest and count the rest dropped,
 * which only a frame far longer than a tick allows: a page receives at most 16 events a tick (ADR-074 decision 4).
 */
export class HitFeed {
  private readonly ring: HitReading[] = [];
  private head = 0;
  private size = 0;
  private droppedCount = 0;

  /**
   * @param capacity The most hits held between two drains.
   * @throws RangeError when the capacity is not a whole number of at least 1.
   */
  public constructor(private readonly capacity: number) {
    if (!Number.isInteger(capacity) || capacity < 1) {
      throw new RangeError(`a hit feed of ${capacity} holds nothing`);
    }
    for (let index = 0; index < capacity; index += 1) {
      this.ring.push({ target: 0, amount: 0, critical: false, taken: false, damageType: 0, cause: 0, heal: false });
    }
  }

  /**
   * Hits dropped so far because the ring was full.
   *
   * @returns The count.
   */
  public get dropped(): number {
    return this.droppedCount;
  }

  /**
   * Queues a hit.
   *
   * @param target The handle the hit landed on, over which its number stands.
   * @param amount The damage.
   * @param critical Whether it was a critical hit.
   * @param taken Whether the player took it, rather than dealt it.
   * @param damageType The damage type the event's flags carry (0 physical, 1 thermal, 2 kinetic, 3 poison).
   * @param cause Why a hit of 0 dealt nothing (`NegationCause`), 0 for none.
   * @param heal Whether it restored health rather than dealt damage.
   */
  public push(target: number, amount: number, critical: boolean, taken: boolean, damageType: number, cause: number, heal: boolean): void {
    if (this.size === this.capacity) {
      this.head = (this.head + 1) % this.capacity;
      this.size -= 1;
      this.droppedCount += 1;
    }
    const slot = this.ring[(this.head + this.size) % this.capacity] as HitReading;
    slot.target = target;
    slot.amount = amount;
    slot.critical = critical;
    slot.taken = taken;
    slot.damageType = damageType;
    slot.cause = cause;
    slot.heal = heal;
    this.size += 1;
  }

  /**
   * Hands every waiting hit to a visitor, oldest first, and empties the ring. The record handed is reused.
   *
   * @param visit Told each hit.
   */
  public drain(visit: (hit: Readonly<HitReading>) => void): void {
    while (this.size > 0) {
      const slot = this.ring[this.head] as HitReading;
      this.head = (this.head + 1) % this.capacity;
      this.size -= 1;
      visit(slot);
    }
  }

  /**
   * Forgets every waiting hit.
   */
  public clear(): void {
    this.head = 0;
    this.size = 0;
  }
}
